import type React from "react";
import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, FileText, Menu, X, LogOut } from "lucide-react";
import AuthWrapper from "../AuthWrapper";
import Logo from "../assets/apple-touch-icon.png";

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Past Reports", path: "/dashboard/past-reports", icon: FileText },
  ];

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const getPageTitle = () => {
    const path = location.pathname;

    if (path === "/dashboard") {
      return "Dashboard";
    }

    const matchingNavItem = navItems.find((item) => item.path === path);
    if (matchingNavItem) {
      return matchingNavItem.name;
    }

    const pathSegments = path.split("/").filter(Boolean);
    if (pathSegments.length > 1) {
      const lastSegment = pathSegments[pathSegments.length - 1];

      return lastSegment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    return "Dashboard";
  };

  return (
    <AuthWrapper>
      <div className="flex h-screen bg-gray-50">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
            onClick={toggleSidebar}
          ></div>
        )}

        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-gradient-to-b from-white to-red-50 shadow-lg transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-full flex-col">
            <div className="flex h-16 items-center justify-between px-6">
              {/* Improved logo placement */}
              <div className="flex justify-center">
                <div className="h-16 w-16 relative">
                  <img
                    src={Logo || "/placeholder.svg"}
                    alt="Zenith Logo"
                    className="object-contain rounded-lg"
                  />
                </div>
              </div>
              <button
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-600 lg:hidden"
                onClick={toggleSidebar}
              >
                <X size={20} />
              </button>
            </div>

            <nav className="mt-8 px-4 flex-1">
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.path}
                      end={item.path === "/dashboard"}
                      className={({ isActive }) =>
                        `flex items-center rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-red-100 text-red-600"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`
                      }
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Bottom section with portal name and logout */}
            <div className="px-4 pb-4">
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => navigate("/login")}
                  className="flex w-full items-center rounded-md px-4 py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  Logout
                </button>
              </div>
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500 font-medium">
                  Zenith Bank Report Portal
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-gradient-to-r from-white to-red-100 shadow-sm">
            <div className="flex h-16 items-center justify-between px-6">
              <button
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-600 lg:hidden"
                onClick={toggleSidebar}
              >
                <Menu size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-800 capitalize">
                {getPageTitle()}
              </h1>
              <div className="flex items-center">
                <button
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-red-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 active:bg-red-800"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default DashboardLayout;
